// src/components/EmailVerificationHandler.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '../styles/EmailVerificationHandler.module.css';

const EmailVerificationHandler = () => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleVerification = async () => {
      try {
        // Extract action code (oobCode) from URL
        const queryParams = new URLSearchParams(location.search);
        const actionCode = queryParams.get('oobCode');
        const mode = queryParams.get('mode');

        if (!actionCode || mode !== 'verifyEmail') {
          setStatus('error');
          setError('Invalid verification link. Please try again with a new verification email.');
          return;
        }

        // Apply the action code
        await verifyEmail(actionCode);
        setStatus('success');
        
        // Auto-redirect after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } catch (error) {
        console.error('Email verification error:', error);
        setStatus('error');
        
        if (error.code === 'auth/expired-action-code') {
          setError('This verification link has expired. Please request a new verification email.');
        } else if (error.code === 'auth/invalid-action-code') {
          setError('Invalid verification link. Please try again with a new verification email.');
        } else if (error.code === 'auth/user-not-found') {
          setError('The user account could not be found. Please sign up again.');
        } else {
          setError('Failed to verify email. Please try again later.');
        }
      }
    };

    handleVerification();
  }, [location, verifyEmail, navigate]);

  // Render content based on status
  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <>
            <div className={styles.spinner}></div>
            <h2>Verifying your email...</h2>
            <p>Please wait while we confirm your email address.</p>
          </>
        );
      
      case 'success':
        return (
          <>
            <div className={styles.successIcon}>✓</div>
            <h2>Email Verified!</h2>
            <p>Your email has been successfully verified.</p>
            <p>You will be redirected to your dashboard in a few seconds.</p>
            <button 
              className={styles.button}
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </button>
          </>
        );
      
      case 'error':
        return (
          <>
            <div className={styles.errorIcon}>✗</div>
            <h2>Verification Failed</h2>
            <p className={styles.errorMessage}>{error}</p>
            <button 
              className={styles.button}
              onClick={() => navigate('/login')}
            >
              Return to Login
            </button>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {renderContent()}
      </div>
    </div>
  );
};

export default EmailVerificationHandler;